import { HideAllLinesCommand } from "slotworks/commands/lines/hide-all-lines-command";
import { ShowLinesCommand } from "slotworks/commands/lines/show-lines-command";
import { DimSymbolsCommand } from "slotworks/commands/symbols/dim-symbols-command";
import { StaticSymbolsCommand } from "slotworks/commands/symbols/static-symbols-command";
import { WinningSymbolsCommand } from "slotworks/commands/symbols/winning-symbols-command";
import Components from "slotworks/components/components";
import AbstractMediator from "slotworks/mediators/abstract-mediator";
import GameState from "slotworks/model/game-state";
import LineResult from "slotworks/model/gameplay/records/results/line-result";
import SymbolWinResult from "slotworks/model/gameplay/records/results/symbol-win-result";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import SlotDefinition from "slotworks/model/slot-definition";
import { SoundEvent } from "slotworks/sound/sound-events";
import SoundManager from "slotworks/sound/sound-manager";
import { reject } from "slotworks/utils/collection-utils";
import Contract from "slotworks/utils/contract";
import { parallel } from "slotworks/utils/race-utils";
import Timer from "slotworks/utils/timer";
import { PlaySymbolSoundCommand } from "../commands/symbols/play-symbol-sound-command";
import SettingsModel from "slotworks/model/settings/settings-model";
import { DisplaySymbolsCommand } from "slotworks/commands/symbols/display-symbols-command";

export default class CycleWinsMediator extends AbstractMediator {

    public static DELAY: number = 0;

    private wins: SymbolWinResult[];
    private currentWin: number;
    private lowWinOnUk: boolean;

    public start(): Contract<void> {
        if (GameState.currentGameHasSymbolWinResults()) {
            const record = GameState.getCurrentGame().getCurrentRecord() as SpinRecord;

            record.timesCycled++;

            this.wins = record.getResultsOfType(SymbolWinResult) as SymbolWinResult[];

            this.currentWin = 0;

            this.lowWinOnUk = SettingsModel.isUKJurisdiction() && !GameState.getCurrentGame().isWinAboveBet();

            return new Contract<void>((resolve) => {
                this.nextWin(resolve);
            });
        } else {
            return super.start();
        }
    }

    public end(): Contract<void> {
        return new Contract<void>((resolve) => {
            this.wins = null;
            StaticSymbolsCommand().execute();
            HideAllLinesCommand().execute();
            Components.celebrateWin.hide();
            resolve(null);
        });
    }

    private nextWin(onComplete: Function) {

        const raceFuncs: Array<() => Contract<any>> = [];

        const win = this.wins[this.currentWin];
        const symbol = SlotDefinition.getSymbolDefinition(win.symbolType);
        const symbolName = symbol ? symbol.name : "";

        const singleLineWinOnUk = this.wins.length == 1 && this.lowWinOnUk;

        if (win instanceof LineResult) {
            const lineDef = SlotDefinition.lineDefinitions[win.line - 1];
            const middleSymbolRow = lineDef[Math.floor(lineDef.length * 0.5)].y + 1;

            ShowLinesCommand([win.line]).execute();
            raceFuncs.push(() => Components.celebrateWin.lineWin(win.creditsWon, win.line, symbolName, win.matches, middleSymbolRow, singleLineWinOnUk));
        } else if (win.creditsWon > 0) {
            raceFuncs.push(() => Components.celebrateWin.lineWin(win.creditsWon, null, symbolName, win.matches));
        }

        raceFuncs.push(() => this.showWin(win));

        parallel(raceFuncs).then(
            () => {
                if(singleLineWinOnUk) {
                    // single line win uk jurisdiction to avoid flickering of win
                } else {
                    Timer.setTimeout(() => {
                        if (!this.wins) {
                            return;
                        }

                        Components.matrix.idleSymbols();
                        HideAllLinesCommand().execute();

                        this.currentWin++;
                        if (this.currentWin < this.wins.length) {
                            if (this.nextWin) {
                                this.nextWin(onComplete);
                            }
                        } else {
                            onComplete();
                        }
                    }, CycleWinsMediator.DELAY);
                }   
            }
        );
    }

    private showWin(win: SymbolWinResult): Contract<void> {
        const record = GameState.getCurrentGame().getCurrentRecord() as SpinRecord;

        const winningSymbols = [];

        for (const position of win.positions) {
            const symbol = Components.matrix.getSymbol(position.x, position.y);
            winningSymbols.push(symbol);
        }

        const currentCycle = record.timesCycled;

        if(this.lowWinOnUk) {
            if(currentCycle == 1)
                SoundManager.event(SoundEvent.symbol_win_below_threshold);
        } else {
            PlaySymbolSoundCommand(win.symbolType, win.matches.toString(), currentCycle.toString());
        }

        const remainingSymbols = reject(Components.matrix.getAllSymbols(), winningSymbols);

        DimSymbolsCommand(remainingSymbols).execute();

        if(this.lowWinOnUk)
            return DisplaySymbolsCommand(winningSymbols);
        else 
            return WinningSymbolsCommand(winningSymbols, record.beenPaid);
    }
}



// WEBPACK FOOTER //
// ./~/slotworks/mediators/loop-wins-mediator.ts